import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo, { SITE_URL } from "@/components/base/Seo";
import type { DirectoryLocation } from "@/types/directory";
import { getLocationBySlug, getTownsByDistrict } from "@/services/directoryService";
import BusinessExplorer from "@/components/feature/BusinessExplorer";
import DistrictView from "./components/DistrictView";
import PromoBand from "./components/PromoBand";
import { useDistrictPromos } from "@/hooks/useDistrictPromos";

export default function LocationPage() {
  const { slug = "" } = useParams();
  const [location, setLocation] = useState<DirectoryLocation | null>(null);
  const [towns, setTowns] = useState<DirectoryLocation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getLocationBySlug(slug)
      .then(async (result) => {
        if (!active) return;
        setLocation(result);
        if (result?.type === "district") {
          const districtTowns = await getTownsByDistrict(result.slug);
          if (active) setTowns(districtTowns);
        } else {
          setTowns([]);
        }
      })
      .catch(() => {
        if (active) {
          setLocation(null);
          setTowns([]);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  const townDistrictSlug = location?.type === "town" ? location.districtSlug : "";
  const { sponsored, featured, loading: promoLoading } = useDistrictPromos(townDistrictSlug);
  const showPromoBand = promoLoading || sponsored.length > 0 || featured.length > 0;

  if (!loading && !location) {
    return (
      <SiteLayout>
        <section className="mx-auto flex w-full max-w-[1400px] flex-col items-center justify-center px-6 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-map-pin-2-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            Location not found
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-600">
            We could not find that location. Browse all the districts and towns we cover instead.
          </p>
          <Link
            to="/locations"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            View all locations
          </Link>
        </section>
      </SiteLayout>
    );
  }

  const isDistrict = location?.type === "district";

  return (
    <SiteLayout>
      <Seo
        title={`Businesses in ${location?.name ?? "Coastal Karnataka"}`}
        description={
          isDistrict
            ? `Browse verified local businesses across every town and locality in ${location?.name}, coastal Karnataka. Compare ratings, hours and contact details.`
            : `Discover verified local businesses operating in ${location?.name}, coastal Karnataka — with reviews, working hours and direct contact.`
        }
        keywords={`businesses in ${location?.name ?? "Karnataka"}, ${location?.name ?? "coastal Karnataka"} directory, local businesses ${location?.name ?? "Karnataka"}`}
        path={`/location/${slug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `Businesses in ${location?.name ?? "Coastal Karnataka"}`,
          url: `${SITE_URL}/location/${slug}`,
        }}
      />
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-12">
          <nav
            className="flex flex-wrap items-center gap-2 text-xs text-foreground-500"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="transition-colors hover:text-primary-700">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <Link to="/locations" className="transition-colors hover:text-primary-700">
              Locations
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-foreground-700">{location?.name ?? slug}</span>
          </nav>

          <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
            Businesses in {location?.name ?? "this location"}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {isDistrict
              ? `Browse verified local businesses across every town and locality in ${location?.name} — explore the map, pick a taluk and filter by rating below.`
              : `Discover verified local businesses operating in ${location?.name}, coastal Karnataka.`}
          </p>
          {location ? (
            <p className="mt-3 flex items-center gap-2 text-sm font-medium text-secondary-700">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-store-3-line text-base" />
              </span>
              {location.businessCount.toLocaleString("en-IN")} businesses listed here
            </p>
          ) : null}
        </div>
      </section>

      {isDistrict && location ? (
        <DistrictView district={location} taluks={towns} />
      ) : (
        <>
          {showPromoBand ? (
            <section className="mx-auto w-full max-w-[1400px] px-4 pt-10 md:px-6 md:pt-12">
              <PromoBand sponsored={sponsored} featured={featured} loading={promoLoading} />
            </section>
          ) : null}
          <section className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-6 md:py-12">
            <BusinessExplorer lockedLocation={slug} showDistrictFilter={false} pageSize={9} />
          </section>
        </>
      )}
    </SiteLayout>
  );
}